import React, { useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { printElement } from "../printing";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary } from "../theme";

// A QR code drawn as one SVG path (crisp at any print size; no image files,
// no outside service — the code is generated right here in the browser).
export function QrCode({ text, size = "5cm" }) {
  const qr = useMemo(() => QRCode.create(text, { errorCorrectionLevel: "M" }), [text]);
  const quiet = 2; // quiet-zone modules on every side, as scanners expect
  const n = qr.modules.size;
  const total = n + quiet * 2;
  let d = "";
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (qr.modules.data[y * n + x]) d += `M${x + quiet},${y + quiet}h1v1h-1z`;
    }
  }
  return (
    <svg
      viewBox={`0 0 ${total} ${total}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code for ${text}`}
      data-qr-text={text}
      style={{ flexShrink: 0, display: "block" }}
    >
      <rect width={total} height={total} fill="#ffffff" />
      <path d={d} fill="#000000" />
    </svg>
  );
}

// Preview + print of one page holding the Waiter, Kitchen and Manager login
// QR codes. Codes can be left off the sheet (e.g. keep the Manager code off a
// wall customers can see).
export default function QrSheetModal({ businessName, links, onClose, title = "Print QR codes", intro = "One page with a code per login. Staff scan it with their phone camera and land on the right login screen.", subtitle = "Staff login QR codes", instructions = "Scan with your phone camera, then enter your PIN." }) {
  const [included, setIncluded] = useState(() => Object.fromEntries(links.map((l) => [l.key, true])));
  const sheetRef = useRef(null);
  const chosen = links.filter((l) => included[l.key]);

  return (
    <div style={styles.overlay} className="qr-overlay">
      <div style={styles.modal} role="dialog" aria-label={title}>
        <div className="no-print">
          <h2 style={{ fontFamily: fonts.display, fontSize: 18, margin: `0 0 ${space[2]}` }}>{title}</h2>
          <p style={styles.hint}>
            {intro}
          </p>
          <div style={styles.checks}>
            {links.map((l) => (
              <label key={l.key} style={styles.check}>
                <input
                  type="checkbox"
                  checked={included[l.key]}
                  onChange={(e) => setIncluded({ ...included, [l.key]: e.target.checked })}
                />
                {l.label}{l.private ? " (keep private)" : ""}
              </label>
            ))}
          </div>
          <div style={styles.actions}>
            <button
              style={{ ...buttonPrimary, opacity: chosen.length ? 1 : 0.5 }}
              disabled={chosen.length === 0}
              onClick={() => printElement(sheetRef.current, "qr")}
            >
              Print
            </button>
            <button style={buttonSecondary} onClick={onClose}>Close</button>
          </div>
          <p style={styles.hint}>Tip: choose “Save as PDF” in the print dialog to keep a copy.</p>
        </div>

        <div ref={sheetRef} className="qr-sheet" style={styles.sheet}>
          <h1 className="qr-title" style={styles.title}>{businessName}</h1>
          <div style={styles.subtitle}>{subtitle}</div>
          {chosen.map((l) => (
            <div key={l.key} className="qr-block" style={styles.block}>
              <QrCode text={l.url} />
              <div style={{ minWidth: 0, flex: "1 1 200px" }}>
                <div className="qr-label" style={styles.label}>{l.label}</div>
                <div style={styles.instructions}>{instructions}</div>
                {l.private && <div style={styles.private}>Keep private. Managers only.</div>}
                <div style={styles.url}>{l.url}</div>
              </div>
            </div>
          ))}
          {chosen.length === 0 && <p style={styles.hint}>Tick at least one code to print.</p>}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { width: 640, maxWidth: "calc(100vw - 24px)", boxSizing: "border-box", maxHeight: "92vh", overflowY: "auto", background: colors.surface, borderRadius: radius.md, padding: space[5], fontFamily: fonts.body, color: colors.textPrimary },
  hint: { fontSize: 12, color: colors.textMuted, margin: `${space[1]} 0` },
  checks: { display: "flex", gap: space[4], flexWrap: "wrap", margin: `${space[3]} 0` },
  check: { display: "flex", alignItems: "center", gap: 6, fontSize: 13 },
  actions: { display: "flex", gap: space[2], marginBottom: space[2] },
  sheet: { marginTop: space[3], padding: space[4], border: `1px dashed ${colors.border}`, background: "#fff", color: "#000" },
  title: { margin: 0, textAlign: "center", fontFamily: fonts.display, fontSize: 26, color: "#000" },
  subtitle: { textAlign: "center", fontSize: 13, color: "#444", marginBottom: space[3] },
  block: { display: "flex", gap: space[4], alignItems: "center", flexWrap: "wrap", padding: `${space[3]} 0`, borderTop: "1px dashed #bbb" },
  label: { fontFamily: fonts.display, fontSize: 24, fontWeight: 700, color: "#000", textTransform: "uppercase", letterSpacing: "0.04em", wordBreak: "break-word" },
  instructions: { fontSize: 14, color: "#222", margin: "4px 0" },
  private: { fontSize: 12, fontWeight: 700, color: "#7f1d1d", margin: "2px 0" },
  url: { fontSize: 10, color: "#555", wordBreak: "break-all", marginTop: 4 },
};
